import { forwardRef } from 'react';
import { Building2 } from 'lucide-react';
import { getTheme, isLightColor, type ThemePreset } from '@/lib/themes';
import { cn } from '@/lib/utils';

export interface VerticalCardData {
  id?: string;
  slug: string;
  display_name: string;
  title?: string | null;
  company?: string | null;
  photo_url?: string | null;
  company_logo_url?: string | null;
  cover_image_url?: string | null;
  theme_preset?: string | null;
  custom_accent?: string | null;
  custom_bg?: string | null;
  custom_card_bg?: string | null;
  custom_text?: string | null;
}

interface VerticalCardProps {
  card: VerticalCardData;
  className?: string;
  themeOverride?: ThemePreset;
}

export const VerticalCard = forwardRef<HTMLDivElement, VerticalCardProps>(function VerticalCard(
  { card, className, themeOverride },
  ref,
) {
  const theme = themeOverride ?? getTheme(card.theme_preset, card);
  const isLight = isLightColor(theme.cardBg);

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex aspect-[9/14] flex-col overflow-hidden rounded-2xl shadow-[0_20px_50px_-25px_rgba(10,10,10,0.25)]',
        className,
      )}
      style={{ backgroundColor: theme.cardBg, color: theme.text }}
    >
      {/* Cover band */}
      <div
        className="relative h-[38%] w-full"
        style={
          card.cover_image_url
            ? {
                backgroundImage: `url("${card.cover_image_url}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {
                backgroundImage: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.text} 100%)`,
              }
        }
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, rgba(10,10,10,0) 50%, rgba(10,10,10,0.18) 100%)',
          }}
        />
      </div>

      {/* Photo + identity */}
      <div className="relative -mt-12 flex flex-1 flex-col px-4">
        <div className="flex items-end gap-3">
          <div
            className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl ring-4"
            style={{
              backgroundColor: theme.accent,
              // @ts-expect-error CSS var cast
              '--tw-ring-color': theme.cardBg,
            }}
          >
            {card.photo_url ? (
              <img
                src={card.photo_url}
                alt={card.display_name}
                className="h-full w-full object-cover"
                draggable={false}
              />
            ) : (
              <div
                className="flex h-full w-full items-center justify-center text-lg font-medium"
                style={{ color: theme.cardBg }}
              >
                {initials(card.display_name)}
              </div>
            )}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-4"
              style={{
                background: `linear-gradient(to right, transparent, ${theme.cardBg})`,
              }}
            />
          </div>

          <div className="min-w-0 flex-1 pb-1">
            <p
              className="truncate text-lg font-medium leading-tight tracking-tightest"
              style={{ color: theme.text }}
            >
              {card.display_name || 'Your name'}
            </p>
            {card.title && (
              <p className="mt-0.5 truncate text-[11px] opacity-75">{card.title}</p>
            )}
          </div>
        </div>

        {(card.company || card.company_logo_url) && (
          <div
            className="mt-4 flex items-center gap-2 rounded-md px-2.5 py-1.5"
            style={{
              backgroundColor: isLight ? 'rgba(10,10,10,0.04)' : 'rgba(255,255,255,0.06)',
            }}
          >
            {card.company_logo_url ? (
              <img
                src={card.company_logo_url}
                alt=""
                className="h-4 w-4 shrink-0 rounded-sm object-contain"
              />
            ) : (
              <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
            )}
            {card.company && (
              <span className="truncate text-[9px] font-medium uppercase tracking-[0.18em] opacity-80">
                {card.company}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <div
            className="h-7 rounded-full text-center text-[10px] font-medium leading-7"
            style={{ backgroundColor: theme.text, color: theme.cardBg }}
          >
            Save contact
          </div>
          <div
            className="h-7 rounded-full border text-center text-[10px] font-medium leading-7"
            style={{ borderColor: theme.text, color: theme.text }}
          >
            Exchange
          </div>
        </div>

        <div className="mt-auto pb-3 pt-3 text-right">
          <p className="font-mono text-[8px] uppercase tracking-widest opacity-50">
            haphe.co/{card.slug || 'your-slug'}
          </p>
        </div>
      </div>
    </div>
  );
});

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}
